import { useEffect, useRef } from 'react';
import TagManager from 'react-gtm-module';
import {
  EventPageAreaName,
  EventPageAreaType,
  GNBEvent,
  HomeEvent,
  HomePageAreaType,
  MypageEvent,
  SalesEvent,
  SalesPageAreaType,
  SNBEventPageArea,
  SocialEvent,
  WorkEvent,
  WorkPageAreaType,
} from '@/constant/gtm';
import { useDebugLogger } from '@/hooks/useDebugLogger';
import { isLocal } from '@/constant';

export type EventTracker<T extends string> = (
  event: T,
  data?: Record<string, any>,
) => void;

export function useGTMEventTracker<T extends string>(
  pageArea?: EventPageAreaType,
): EventTracker<T> {
  const logger = useDebugLogger();
  const isInitialized = useRef(false);

  useEffect(() => {
    // window.dataLayer 존재하면 초기화된 것으로 간주
    isInitialized.current = !!window.dataLayer;
  }, []);

  return (event: T, data?: Record<string, any>) => {
    isLocal && console.log('!!!!', { event, page_area: pageArea, ...data });
    if (!isInitialized.current) {
      console.warn('GTM not initialized. Event not sent.');
      return;
    }

    // 정의된 pageArea 없는 경우 이벤트 전송하지 않음
    if (!pageArea && !data?.page_area) return;

    const dataLayer = {
      event,
      page_area: pageArea,
      ...data,
    };
    TagManager.dataLayer({ dataLayer });

    logger.log(dataLayer);
  };
}

export const getPrevPageArea = () => {
  let prevPageArea: null | EventPageAreaType = null;
  if (window.dataLayer && window.dataLayer.length > 0) {
    const latest = window.dataLayer.findLast((item) => {
      if (typeof item.event !== 'string') return false;

      const event = item.event as string;
      return (
        event !== 'page_view' && !event.startsWith('gtm.') && item.page_area
      );
    });
    if (latest) prevPageArea = latest.page_area;
  }
  return prevPageArea;
};

export const useGNBEventTracker = () =>
  useGTMEventTracker<GNBEvent>(EventPageAreaName.GNB);

export const useSNBEventTracker = (pageArea: SNBEventPageArea) =>
  useGTMEventTracker<HomeEvent | WorkEvent>(pageArea);

export const useHomeEventTracker = (pageArea: HomePageAreaType) =>
  useGTMEventTracker<HomeEvent>(pageArea);

export const useWorkEventTracker = (pageArea: WorkPageAreaType) =>
  useGTMEventTracker<WorkEvent>(pageArea);

export const useMypageEventTracker = () =>
  useGTMEventTracker<MypageEvent>(EventPageAreaName.MYPAGE);

export const useSocialEventTracker = () =>
  useGTMEventTracker<SocialEvent>(EventPageAreaName.SOCIAL);

export const useSalesEventTracker = (pageArea: SalesPageAreaType) =>
  useGTMEventTracker<SalesEvent>(pageArea);
